import { useCallback, useEffect, useState } from 'react'
import { supabase, readableError } from '../lib/supabase'

/** Turn a user-agent string into something a person recognises. */
function describe(ua = '') {
  const s = String(ua)
  const device =
    /iPhone/i.test(s) ? 'iPhone' :
    /iPad/i.test(s) ? 'iPad' :
    /Android/i.test(s) ? 'Android phone' :
    /Macintosh|Mac OS/i.test(s) ? 'Mac' :
    /Windows/i.test(s) ? 'Windows PC' :
    /Linux/i.test(s) ? 'Linux' :
    /curl|node/i.test(s) ? 'Script' : 'Device'
  const browser =
    /CriOS|Chrome/i.test(s) ? 'Chrome' :
    /FxiOS|Firefox/i.test(s) ? 'Firefox' :
    /Edg/i.test(s) ? 'Edge' :
    /Safari/i.test(s) ? 'Safari' : null
  return browser ? `${device} · ${browser}` : device
}

const when = (iso) => {
  const mins = Math.floor((Date.now() - new Date(iso)) / 60000)
  if (mins < 2) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const h = Math.floor(mins / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return d === 1 ? 'yesterday' : `${d} days ago`
}

/**
 * Where the account is signed in. Sessions here are the real ones — revoking a
 * row ends that device's access, it does not just hide it from a list.
 */
export default function Devices() {
  const [rows, setRows] = useState(null)
  const [busy, setBusy] = useState(null)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('my_sessions')
    if (error) return setErr(readableError(error))
    setRows(data ?? [])
  }, [])

  useEffect(() => { load() }, [load])

  const revoke = async (id) => {
    setBusy(id); setErr(null)
    const { error } = await supabase.rpc('revoke_session', { p_session: id })
    setBusy(null)
    if (error) return setErr(readableError(error))
    load()
  }

  const revokeOthers = async () => {
    setBusy('all'); setErr(null)
    const { error } = await supabase.rpc('revoke_other_sessions')
    setBusy(null)
    if (error) return setErr(readableError(error))
    load()
  }

  if (!rows) return <p className="small">Checking…</p>

  const others = rows.filter((r) => !r.is_current).length

  return (
    <>
      <h3 className="sechead">
        Devices
        <span className="note">
          You stay signed in on a device until you sign out here. If you lose a
          phone, this is the page that matters.
        </span>
      </h3>

      <div className="rows">
      {!rows.length && (
        <div className="rowempty">No other signed-in devices.</div>
      )}
      {rows.map((r) => (
        <div key={r.session_id} className={`cosign${r.is_current ? ' signed' : ''}`}>
          <div className="who">
            <div className="n">{describe(r.user_agent)}</div>
            <div className="st">
              {r.is_current ? 'This device' : `Last used ${when(r.last_seen_at)}`}
              {r.ip ? ` · ${r.ip}` : ''}
            </div>
          </div>
          {r.is_current
            ? <span className="badge">Now</span>
            : <button className="iconbtn" disabled={busy === r.session_id}
                      onClick={() => revoke(r.session_id)}>
                {busy === r.session_id ? '…' : 'Sign out'}
              </button>}
        </div>
      ))}

      {others > 0 && (
        <button className="action destroy" disabled={busy === 'all'} onClick={revokeOthers}>
          <span>{busy === 'all'
            ? 'Signing out…'
            : `Sign out the other ${others} device${others === 1 ? '' : 's'}`}</span>
          <span className="ar">›</span>
        </button>
      )}
      </div>

      {err && <p className="err" style={{ marginTop: -14, marginBottom: 26 }}>{err}</p>}
    </>
  )
}
